import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_POLL_OPTIONS,
  MAX_POLL_OPTION_TEXT,
  MAX_POLL_QUESTION,
  pollSharePercent,
  type Huddle,
  type HuddlePoll,
} from '@avslack/shared';
import { useUsers } from '../data/users';
import {
  ApiRequestError,
  closeHuddlePoll,
  getHuddlePoll,
  startHuddlePoll,
  voteHuddlePoll,
} from '../lib/api';
import { Dialog } from './Dialog';
import { Icon } from './Icon';

/**
 * SZAVAZAS az olelesben.
 *
 * Barki inditja, aki bent van; csak a hivasban latszik, es amikor lezarjak (a keszito
 * vagy a hazigazda), az EREDMENY kerul a csatorna szovegfolyamaba - a tobb DM-bol
 * osszeallt hivasnal a behozott resztvevok DM-jebe is.
 */

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}

// ─── Inditas ──────────────────────────────────────────────────────────────────

export function HuddlePollStartDialog({
  open,
  onOpenChange,
  huddleId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  huddleId: string;
}) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filled = options.map((text) => text.trim()).filter(Boolean);
  const valid = question.trim().length > 0 && filled.length >= 2;

  const reset = () => {
    setQuestion('');
    setOptions(['', '']);
    setMultiple(false);
    setAnonymous(false);
    setError(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await startHuddlePoll(huddleId, {
        question: question.trim(),
        options: filled,
        multiple,
        anonymous,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, 'A szavazás indítása nem sikerült.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Szavazás indítása"
      description="Mindenki a hívásban szavazhat. A lezáráskor az eredmény a csatornába kerül."
    >
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-ink-muted">Kérdés</span>
          <input
            autoFocus
            value={question}
            maxLength={MAX_POLL_QUESTION}
            onChange={(event) => setQuestion(event.target.value)}
            className="w-full rounded-control border border-border bg-surface px-3 py-2"
          />
        </label>

        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-xs font-semibold text-ink-muted">Válaszlehetőségek</legend>
          {options.map((text, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                value={text}
                maxLength={MAX_POLL_OPTION_TEXT}
                placeholder={`${index + 1}. lehetőség`}
                aria-label={`${index + 1}. lehetőség`}
                onChange={(event) =>
                  setOptions((current) =>
                    current.map((value, i) => (i === index ? event.target.value : value)),
                  )
                }
                className="min-w-0 flex-1 rounded-control border border-border bg-surface px-3 py-1.5"
              />
              {options.length > 2 && (
                <button
                  type="button"
                  aria-label={`${index + 1}. lehetőség törlése`}
                  onClick={() => setOptions((current) => current.filter((_, i) => i !== index))}
                  className="rounded-control px-2 py-1 text-ink-muted hover:bg-surface-hover hover:text-ink"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {options.length < MAX_POLL_OPTIONS && (
            <button
              type="button"
              onClick={() => setOptions((current) => [...current, ''])}
              className="rounded-control px-2 py-1 text-xs font-semibold text-accent hover:bg-surface-hover"
            >
              + Újabb lehetőség
            </button>
          )}
          <p className="text-2xs text-ink-faint">
            {filled.length} lehetőség kitöltve (legfeljebb {MAX_POLL_OPTIONS}).
          </p>
        </fieldset>

        <div className="flex flex-wrap gap-4 text-xs text-ink-muted">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={multiple}
              onChange={(event) => setMultiple(event.target.checked)}
            />
            Több válasz is adható
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
            />
            Névtelen
          </label>
        </div>

        {error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-control px-3 py-2 text-xs text-ink-muted hover:bg-surface-hover"
          >
            Mégsem
          </button>
          <button
            type="button"
            disabled={busy || !valid}
            onClick={() => void submit()}
            className="rounded-control bg-accent px-3 py-2 text-xs font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? 'Indítás…' : 'Szavazás indítása'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── A nyitott szavazas ───────────────────────────────────────────────────────

/** A hivasban lathato, nyitott szavazas: kerdes, elo megoszlas, szavazas, lezaras. */
export function HuddlePollPanel({
  huddle,
  poll,
  meId,
  onClose,
}: {
  huddle: Huddle;
  poll: HuddlePoll;
  meId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { nameOf } = useUsers();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A sajat szavazataink: nevtelen szavazasnal a szerver nem adja ki a nevsort, ezert kulon kerjuk.
  const key = ['huddle-poll', huddle.id, poll.id] as const;
  const mine = useQuery({
    queryKey: key,
    queryFn: () => getHuddlePoll(huddle.id).then((result) => result.myVotes),
    staleTime: Infinity,
  });
  // Nem nevtelennel az elo megoszlasbol is kiolvashato - az a frissebb.
  const myVotes: string[] = poll.anonymous
    ? (mine.data ?? [])
    : poll.options.filter((option) => option.voterIds.includes(meId)).map((option) => option.id);

  const canClose = poll.createdBy === meId || huddle.hostId === meId;

  const vote = async (optionId: string) => {
    const next = poll.multiple
      ? myVotes.includes(optionId)
        ? myVotes.filter((id) => id !== optionId)
        : [...myVotes, optionId]
      : myVotes.includes(optionId)
        ? []
        : [optionId];
    setBusy(true);
    setError(null);
    try {
      const result = await voteHuddlePoll(huddle.id, next);
      queryClient.setQueryData(key, result.myVotes);
    } catch (err) {
      setError(errorText(err, 'A szavazat nem ment át.'));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await closeHuddlePoll(huddle.id);
    } catch (err) {
      setError(errorText(err, 'A lezárás nem sikerült.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="flex shrink-0 flex-col max-sm:absolute max-sm:inset-0 max-sm:z-30 max-sm:bg-surface-raised border-l border-border-soft sm:w-80">
      <header className="flex items-center gap-2 border-b border-border-soft px-3 py-2">
        <span aria-hidden className="text-accent">
          <Icon name="csillag" />
        </span>
        <p className="min-w-0 flex-1 font-display text-sm font-semibold">Szavazás</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Bezárás"
          className="rounded-control px-2 py-1 text-xs text-ink-muted hover:bg-surface-hover hover:text-ink"
        >
          ×
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        <div>
          <p className="text-sm font-semibold text-ink">{poll.question}</p>
          <p className="mt-0.5 text-2xs text-ink-faint">
            {poll.multiple ? 'Több válasz is adható' : 'Egy válasz adható'}
            {poll.anonymous ? ' · névtelen' : ''}
          </p>
        </div>

        <ul className="space-y-1.5">
          {poll.options.map((option) => {
            const selected = myVotes.includes(option.id);
            const share = pollSharePercent(option.votes, poll.voterCount);
            return (
              <li key={option.id}>
                <button
                  type="button"
                  disabled={busy}
                  aria-pressed={selected}
                  onClick={() => void vote(option.id)}
                  title={
                    option.voterIds.length > 0
                      ? option.voterIds.map((id) => (id === meId ? 'te' : nameOf(id))).join(', ')
                      : undefined
                  }
                  className={[
                    'relative w-full overflow-hidden rounded-control border px-3 py-2 text-left text-sm transition-colors',
                    selected
                      ? 'border-accent text-ink'
                      : 'border-border-soft text-ink hover:border-border-strong',
                  ].join(' ')}
                >
                  {/* A megoszlas: halvany sav a gomb mogott. */}
                  <span
                    aria-hidden
                    className={`absolute inset-y-0 left-0 ${selected ? 'bg-accent-soft' : 'bg-surface-sunken'}`}
                    style={{ width: `${share}%` }}
                  />
                  <span className="relative flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">
                      {selected && <span aria-hidden>✓ </span>}
                      {option.text}
                    </span>
                    <span className="shrink-0 text-2xs tabular-nums text-ink-muted">
                      {option.votes} · {share}%
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <p className="text-2xs text-ink-faint">
          {poll.voterCount === 0 ? 'Még senki nem szavazott.' : `${poll.voterCount} fő szavazott.`}
        </p>

        {error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {error}
          </p>
        )}
      </div>

      <footer className="space-y-2 border-t border-border-soft p-3">
        {canClose ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void close()}
            className="w-full rounded-control bg-accent px-3 py-2 text-xs font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
          >
            Lezárás — az eredmény a csatornába kerül
          </button>
        ) : (
          <p className="text-2xs text-ink-faint">
            Az eredmény a csatornába kerül, amikor a készítő vagy a házigazda lezárja.
          </p>
        )}
      </footer>
    </aside>
  );
}
